/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { GOAL_DOMAINS, MEASUREMENT_TYPES, type CreateItpInput } from '@therapy/shared-validators';
import { Trash2 } from 'lucide-react';
import { useCallback, type JSX } from 'react';
import {
  type FieldErrors,
  type FieldPath,
  type UseFormRegister,
  type UseFormRegisterReturn,
  type UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { type TaxonomyGoal } from '../../lib/api/itp.js';

import { GoalTaxonomyPicker } from './goal-taxonomy-picker.js';
import { emptyToUndefined, stepsFromText, tagsFromText } from './plan-form-utils.js';

export type Register = UseFormRegister<CreateItpInput>;
export type Errors = FieldErrors<CreateItpInput>;
export type SetValue = UseFormSetValue<CreateItpInput>;

/** Typed `goals[i].<key>` path built without a number-in-template (lint) literal. */
function goalPath(index: number, key: string): FieldPath<CreateItpInput> {
  return ('goals.' + String(index) + '.' + key) as FieldPath<CreateItpInput>;
}

interface SelectProps {
  readonly id: string;
  readonly label: string;
  readonly placeholder: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly registration: UseFormRegisterReturn;
}

/** Labelled, optional `<select>` matching the builder's input styling. */
function GoalSelect({ id, label, placeholder, options, registration }: SelectProps): JSX.Element {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
      >
        {label}
      </label>
      <select
        id={id}
        defaultValue=""
        className="focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        {...registration}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** The measurable-outcome fields: domain, typed method + detail, baseline/target, review. */
function GoalMeasurementFields({
  index,
  register,
}: {
  readonly index: number;
  readonly register: Register;
}): JSX.Element {
  const { t } = useTranslation();
  const n = String(index);
  const domainOptions = GOAL_DOMAINS.map((d) => ({
    value: d,
    label: t(`therapist.planForm.domains.${d}`),
  }));
  const methodOptions = MEASUREMENT_TYPES.map((m) => ({
    value: m,
    label: t(`measurementType.${m}`),
  }));
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <GoalSelect
          id={`goal-${n}-domain`}
          label={t('therapist.planForm.domain')}
          placeholder={t('therapist.planForm.selectDomain')}
          options={domainOptions}
          registration={register(goalPath(index, 'domain'), { setValueAs: emptyToUndefined })}
        />
        <GoalSelect
          id={`goal-${n}-mtype`}
          label={t('therapist.planForm.measurementType')}
          placeholder={t('therapist.planForm.selectMeasurementType')}
          options={methodOptions}
          registration={register(goalPath(index, 'measurementType'), {
            setValueAs: emptyToUndefined,
          })}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField
          id={`goal-${n}-measure`}
          type="text"
          label={t('therapist.planForm.measure')}
          autoComplete="off"
          registration={register(goalPath(index, 'measurementMethod'), {
            setValueAs: emptyToUndefined,
          })}
        />
        <FormField
          id={`goal-${n}-review`}
          type="date"
          label={t('therapist.planForm.goalReviewDate')}
          autoComplete="off"
          registration={register(goalPath(index, 'reviewDate'), { setValueAs: emptyToUndefined })}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField
          id={`goal-${n}-baseline`}
          type="text"
          label={t('therapist.planForm.baseline')}
          autoComplete="off"
          registration={register(goalPath(index, 'baseline'), { setValueAs: emptyToUndefined })}
        />
        <FormField
          id={`goal-${n}-target`}
          type="text"
          label={t('therapist.planForm.target')}
          autoComplete="off"
          registration={register(goalPath(index, 'target'), { setValueAs: emptyToUndefined })}
        />
      </div>
    </>
  );
}

interface GoalRowProps {
  readonly index: number;
  readonly register: Register;
  readonly setValue: SetValue;
  readonly errors: Errors;
  readonly taxonomy: readonly TaxonomyGoal[];
  readonly canRemove: boolean;
  readonly onRemove: (index: number) => void;
}

/** One goal block in the ITP builder — measurable fields, approach tags, steps. */
export function GoalRow({
  index,
  register,
  setValue,
  errors,
  taxonomy,
  canRemove,
  onRemove,
}: GoalRowProps): JSX.Element {
  const { t } = useTranslation();
  const n = String(index);
  const remove = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);
  const applyTaxonomy = useCallback(
    (goal: TaxonomyGoal) => {
      const set = (key: string, value: string): void => {
        setValue(goalPath(index, key), value as never, { shouldDirty: true });
      };
      set('title', goal.title);
      set('taxonomyId', goal.id);
      set('domain', goal.domain);
      set('category', t(`therapist.planForm.domains.${goal.domain}`));
      if (goal.defaultMeasurement !== null && goal.defaultMeasurement !== '') {
        set('measurementMethod', goal.defaultMeasurement);
      }
    },
    [setValue, index, t],
  );
  const goalError = errors.goals?.[index];
  return (
    <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">
          {t('therapist.planForm.goalN', { n: index + 1 })}
        </p>
        {canRemove && (
          <button
            type="button"
            onClick={remove}
            className="focus-ring rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-red-500 dark:hover:bg-slate-700"
            aria-label={t('therapist.planForm.removeGoal')}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
      <div className="space-y-3">
        {taxonomy.length > 0 && <GoalTaxonomyPicker taxonomy={taxonomy} onPick={applyTaxonomy} />}
        <input type="hidden" {...register(goalPath(index, 'taxonomyId'))} />
        <input type="hidden" {...register(goalPath(index, 'category'))} />
        <FormField
          id={`goal-${n}-title`}
          type="text"
          label={t('therapist.planForm.goalTitle')}
          autoComplete="off"
          error={goalError?.title?.message}
          registration={register(goalPath(index, 'title'))}
        />
        <GoalMeasurementFields index={index} register={register} />
        <FormField
          id={`goal-${n}-approach`}
          type="text"
          label={t('therapist.planForm.approachTags')}
          placeholder={t('therapist.planForm.approachTagsHint')}
          autoComplete="off"
          registration={register(goalPath(index, 'approachTags'), { setValueAs: tagsFromText })}
        />
        <FormField
          id={`goal-${n}-steps`}
          type="text"
          label={t('therapist.planForm.subTargets')}
          placeholder={t('therapist.planForm.subTargetsHint')}
          autoComplete="off"
          registration={register(goalPath(index, 'steps'), { setValueAs: stepsFromText })}
        />
      </div>
    </div>
  );
}
